import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { appointmentAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Calendar, Clock, MapPin, MessageSquare, Wrench } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import WhatsAppFloat from '@/components/WhatsAppFloat';

const AppointmentPage = () => {
  const [date, setDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    service_type: '',
    full_name: '',
    phone: '',
    address: '',
    notes: '',
    email: '',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchAvailableSlots = async () => {
      try {
        const dateStr = format(date, 'yyyy-MM-dd');
        const response = await appointmentAPI.getAvailableSlots(dateStr);
        setAvailableSlots(response.data.available_slots);
        setSelectedTime('');
      } catch (error) {
        console.error('Error fetching slots:', error);
        toast.error('Saat dilimleri yüklenemedi');
        setAvailableSlots([]);
      }
    };
    if (date) {
      fetchAvailableSlots();
    }
  }, [date]);


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!date || !selectedTime) {
      toast.error('Lütfen tarih ve saat seçin');
      return;
    }

    try {
      setLoading(true);
      const appointmentData = {
        ...formData,
        email: formData.email || null,
        appointment_date: format(date, 'yyyy-MM-dd'),
        appointment_time: selectedTime,
      };

      await appointmentAPI.create(appointmentData);
      toast.success('Randevunuz başarıyla oluşturuldu!');

      // Reset form
      setFormData({
        service_type: '',
        full_name: '',
        phone: '',
        address: '',
        notes: '',
        email: '',
      });
      setDate(null);
      setSelectedTime('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Randevu oluşturulamadı');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>
          Online Servis Randevusu | Aden İklimlendirme İzmir Teknik Servis
        </title>
        <meta
          name="description"
          content="Aden İklimlendirme’den online servis randevusu alın. İzmir genelinde 7/24 klima, kombi ve beyaz eşya teknik servis hizmeti."
        />
      </Helmet>

      <Navbar />

      <section className="py-20 md:py-32 bg-light" data-testid="appointment-page">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-navy mb-4">Online Randevu</h1>
            <p className="text-lg text-slate-600">
              7/24 servis desteği ile klima, kombi ve beyaz eşya arızalarınız için kolayca randevu oluşturabilirsiniz. Uzman ekibimiz en kısa sürede adresinize yönlendirilir.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 rounded-lg shadow-sm" data-testid="appointment-form">
            {/* Service Type */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <Wrench className="inline mr-2" size={18} />
                Hizmet Türü *
              </label>
              <select
                data-testid="service-type-select"
                required
                value={formData.service_type}
                onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange"
              >
                <option value="">Seçiniz</option>
                <option value="klima">Klima Servisi</option>
                <option value="kombi">Kombi Servisi</option>
                <option value="beyaz-esya">Beyaz Eşya Servisi</option>
              </select>
            </div>

            {/* Date Selection */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <Calendar className="inline mr-2" size={18} />
                Tarih Seçin *
              </label>
              <div className="flex justify-center">
                <CalendarComponent
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(date) => date < new Date()}
                  locale={tr}
                  className="rounded-md border"
                />

              </div>
            </div>

            {/* Time Slots */}
            {date && (
              <div className="mb-6">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  <Clock className="inline mr-2" size={18} />
                  Saat Seçin *
                </label>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="time-slots">
                  {availableSlots.map((slot) => (
                    <button
                      key={slot}
                      type="button"
                      data-testid={`time-slot-${slot}`}
                      onClick={() => setSelectedTime(slot)}
                      className={`py-2 rounded-lg font-semibold transition-all ${selectedTime === slot
                        ? 'bg-orange text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
                {availableSlots.length === 0 && (
                  <div className="text-center mt-4">
                    <p className="text-slate-500 mb-3">
                      Bu tarihte müsait saat bulunamadı.
                    </p>
                    <a
                      href="https://wa.me/905063853685"
                      target="_blank"
                      className="btn-secondary inline-flex items-center"
                    >
                      WhatsApp’tan Yaz
                    </a>
                  </div>
                )}

              </div>
            )}

            {/* Contact Information */}
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Ad Soyad *
                </label>
                <input
                  data-testid="full-name-input"
                  type="text"
                  required
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Telefon *
                </label>
                <input
                  data-testid="phone-input"
                  type="tel"
                  required
                  pattern="[0-9]{10,11}"
                  placeholder="05XX XXX XX XX"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                E-posta (Opsiyonel)
              </label>
              <input
                data-testid="email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <MapPin className="inline mr-2" size={18} />
                Adres *
              </label>
              <textarea
                data-testid="address-input"
                required
                rows={3}
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange"
              ></textarea>
            </div>

            <div className="mb-8">
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <MessageSquare className="inline mr-2" size={18} />
                Notlar (Opsiyonel)
              </label>
              <textarea
                data-testid="notes-input"
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange"
              ></textarea>
            </div>

            <button
              data-testid="submit-appointment-button"
              type="submit"
              disabled={loading}
              className="btn-primary w-full text-lg"
            >
              {loading ? 'Oluşturuluyor...' : 'Randevu Oluştur'}
            </button>
          </form>
        </div>
      </section>
      <WhatsAppFloat />
      <Footer />
    </>
  );
};

export default AppointmentPage;
